/*!
 * SkyPlus Contracts Dashboard Component
 * Contract management dashboard with list view, pagination, and BULK DELETE
 * UPDATED: Added checkbox selection for bulk operations
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import { ContractService } from './contract.service';

// Interfaces

interface Contract {
  id: string;
  nodeId: string;
  name: string;
  contractNumber: string;
  contractTitle: string;
  contractType: string;
  contractStatus: string;
  supplierName: string;
  departmentName: string;
  contractAmount: number;
  currency: string;
  effectiveDate: string;
  expiryDate: string;
  priority: string;
  daysToExpiry: number | null;
}

interface ContractStats {
  total: number;
  active: number;
  expiringSoon: number;
  expired: number;
  draft: number;
  pendingReview: number;
  totalValue: number;
}

@Component({
  selector: 'skyplus-contracts',
  templateUrl: './skyplus-contracts.component.html',
  styleUrls: ['./skyplus-contracts.component.scss']
})
export class SkyPlusContractsComponent implements OnInit, OnDestroy {

  // Make Math available in template
  Math = Math;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // State
  isLoading = true;
  errorMessage = '';
  searchTerm = '';
  selectedStatus = 'all';
  selectedType = 'all';
  selectedDepartment = 'all';
  sortBy = 'modifiedAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Data
  contracts: Contract[] = [];
  filteredContracts: Contract[] = [];
  paginatedContracts: Contract[] = [];
  departments: string[] = [];
  
  // Selection for bulk operations
  selectedContracts: Set<string> = new Set();

  stats: ContractStats = {
    total: 0,
    active: 0,
    expiringSoon: 0,
    expired: 0,
    draft: 0,
    pendingReview: 0,
    totalValue: 0
  };

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  paginationStart = 0;
  paginationEnd = 0;

  constructor(
    private router: Router,
    private contractService: ContractService
  ) {}

  ngOnInit(): void {
    this.loadContracts();
    
    // Setup search debounce
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyFilters();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load contracts from Alfresco via ContractService
   */
  loadContracts(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.clearSelection(); // Clear any previous selections

    this.contractService.getAllContracts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.contracts = this.mapContractsResponse(response);
          this.extractDepartments();
          this.calculateStats();
          this.applyFilters();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading contracts:', error);
          this.errorMessage = error.message || 'Failed to load contracts';
          this.isLoading = false;
          this.contracts = [];
          this.filteredContracts = [];
          this.paginatedContracts = [];
        }
      });
  }

  /**
   * Map API response to Contract array
   */
  private mapContractsResponse(response: any): Contract[] {
    let contractsArray: any[] = [];
    
    if (Array.isArray(response)) {
      contractsArray = response;
    } else if (response.contracts) {
      contractsArray = response.contracts;
    } else if (response.list && response.list.entries) {
      contractsArray = response.list.entries.map((e: any) => e.entry);
    }
    
    return contractsArray.map(c => ({
      id: c.id || c.nodeId,
      nodeId: c.nodeId || c.id,
      name: c.name || 'Unknown',
      contractNumber: c.contractNumber || null,
      contractTitle: c.contractTitle || c.name || 'Untitled',
      contractType: c.contractType || 'Other',
      contractStatus: c.contractStatus || c.status || 'Draft',
      supplierName: c.supplierName || null,
      departmentName: c.departmentName || null,
      contractAmount: c.contractAmount || 0,
      currency: c.currency || 'KES',
      effectiveDate: c.effectiveDate || null,
      expiryDate: c.expiryDate || null,
      priority: c.priority || 'Medium',
      daysToExpiry: c.daysToExpiry !== undefined ? c.daysToExpiry : this.calculateDaysToExpiry(c.expiryDate)
    }));
  }

  private calculateDaysToExpiry(expiryDate: string): number | null {
    if (!expiryDate) return null;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      const diff = expiry.getTime() - today.getTime();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  }

  private extractDepartments(): void {
    const depts = new Set<string>();
    this.contracts.forEach(c => {
      if (c.departmentName) {
        depts.add(c.departmentName);
      }
    });
    this.departments = Array.from(depts).sort();
  }

  private calculateStats(): void {
    this.stats = {
      total: this.contracts.length,
      active: this.contracts.filter(c => c.contractStatus === 'Active').length,
      expiringSoon: this.contracts.filter(c => c.contractStatus === 'Expiring Soon').length,
      expired: this.contracts.filter(c => c.contractStatus === 'Expired').length,
      draft: this.contracts.filter(c => c.contractStatus === 'Draft').length,
      pendingReview: this.contracts.filter(c => c.contractStatus === 'Pending Review').length,
      totalValue: this.contracts.reduce((sum, c) => sum + (c.contractAmount || 0), 0)
    };
  }

  // ========== SELECTION METHODS ==========
  
  isSelected(contract: Contract): boolean {
    return this.selectedContracts.has(contract.nodeId);
  }

  toggleSelection(contract: Contract, event: Event): void {
    event.stopPropagation();
    if (this.selectedContracts.has(contract.nodeId)) {
      this.selectedContracts.delete(contract.nodeId);
    } else {
      this.selectedContracts.add(contract.nodeId);
    }
  }

  isAllSelected(): boolean {
    return this.paginatedContracts.length > 0 && 
           this.paginatedContracts.every(c => this.selectedContracts.has(c.nodeId));
  }

  isSomeSelected(): boolean {
    const selectedOnPage = this.paginatedContracts.filter(c => this.selectedContracts.has(c.nodeId)).length;
    return selectedOnPage > 0 && selectedOnPage < this.paginatedContracts.length;
  }

  toggleSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      // Select all on current page
      this.paginatedContracts.forEach(c => this.selectedContracts.add(c.nodeId));
    } else {
      // Deselect all on current page
      this.paginatedContracts.forEach(c => this.selectedContracts.delete(c.nodeId));
    }
  }

  clearSelection(): void {
    this.selectedContracts.clear();
  }

  deleteSelectedContracts(): void {
    const count = this.selectedContracts.size;
    if (count === 0) return;

    const message = count === 1 
      ? 'Are you sure you want to delete this contract?' 
      : `Are you sure you want to delete ${count} contracts?`;

    if (confirm(message)) {
      const deleteObservables = Array.from(this.selectedContracts).map(nodeId => 
        this.contractService.deleteContract(nodeId)
      );

      forkJoin(deleteObservables)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // Remove deleted contracts from local arrays
            this.contracts = this.contracts.filter(c => !this.selectedContracts.has(c.nodeId));
            this.clearSelection();
            this.calculateStats();
            this.applyFilters();
          },
          error: (err) => {
            console.error('Error deleting contracts:', err);
            alert('Failed to delete some contracts. Please try again.');
            // Refresh to get current state
            this.loadContracts();
          }
        });
    }
  }

  // ========== FILTERING ==========
  applyFilters(): void {
    let result = [...this.contracts];

    // Search filter
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.contractTitle && c.contractTitle.toLowerCase().includes(term)) ||
        (c.contractNumber && c.contractNumber.toLowerCase().includes(term)) ||
        (c.supplierName && c.supplierName.toLowerCase().includes(term)) ||
        (c.name && c.name.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (this.selectedStatus !== 'all') {
      result = result.filter(c => c.contractStatus === this.selectedStatus);
    }

    // Type filter
    if (this.selectedType !== 'all') {
      result = result.filter(c => c.contractType === this.selectedType);
    }

    // Department filter
    if (this.selectedDepartment !== 'all') {
      result = result.filter(c => c.departmentName === this.selectedDepartment);
    }

    // Sorting
    result = this.sortContracts(result);

    this.filteredContracts = result;
    this.currentPage = 1;
    this.updatePagination();
  }

  private sortContracts(contracts: Contract[]): Contract[] {
    return contracts.sort((a, b) => {
      let aVal: any = (a as any)[this.sortBy];
      let bVal: any = (b as any)[this.sortBy];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Handle dates
      if (this.sortBy.includes('Date') || this.sortBy.includes('At')) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      // Handle numbers
      if (this.sortBy === 'contractAmount' || this.sortBy === 'daysToExpiry') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  toggleSort(column: string): void {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.applyFilters();
  }

  // ========== PAGINATION ==========
  updatePagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredContracts.length / this.pageSize));
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    
    this.paginationStart = (this.currentPage - 1) * this.pageSize;
    this.paginationEnd = Math.min(this.paginationStart + this.pageSize, this.filteredContracts.length);
    
    this.paginatedContracts = this.filteredContracts.slice(this.paginationStart, this.paginationEnd);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  // ========== FILTER HANDLERS ==========
  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  setStatusFilter(status: string): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  hasFiltersApplied(): boolean {
    return this.searchTerm !== '' || 
           this.selectedStatus !== 'all' || 
           this.selectedType !== 'all' ||
           this.selectedDepartment !== 'all';
  }

  // ========== ACTIONS ==========
  createContract(): void {
    this.router.navigate(['/contract-wizard']);
  }

  uploadExecutedContract(): void {
    this.router.navigate(['/contracts/upload-executed']);
  }

  viewContract(contract: Contract): void {
    this.router.navigate(['/contract-details', contract.nodeId]);
  }

  editContract(contract: Contract): void {
    this.router.navigate(['/contract-wizard', contract.nodeId]);
  }

  deleteContract(contract: Contract): void {
    const title = contract.contractTitle || contract.name;
    if (confirm(`Are you sure you want to delete "${title}"?`)) {
      this.contractService.deleteContract(contract.nodeId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.contracts = this.contracts.filter(c => c.nodeId !== contract.nodeId);
            this.selectedContracts.delete(contract.nodeId);
            this.calculateStats();
            this.applyFilters();
          },
          error: (err) => {
            console.error('Error deleting contract:', err);
            alert('Failed to delete contract: ' + err.message);
          }
        });
    }
  }

  refreshContracts(): void {
    this.loadContracts();
  }

  exportContracts(): void {
    const headers = ['Contract No', 'Title', 'Type', 'Supplier', 'Department', 'Value', 'Currency', 'Expiry Date', 'Days Left', 'Status'];
    const rows = this.filteredContracts.map(c => [
      c.contractNumber || '',
      c.contractTitle || c.name || '',
      c.contractType || '',
      c.supplierName || '',
      c.departmentName || '',
      c.contractAmount || '',
      c.currency || 'KES',
      c.expiryDate ? this.formatDate(c.expiryDate) : '',
      c.daysToExpiry !== null ? c.daysToExpiry : '',
      c.contractStatus || ''
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contracts-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // ========== FORMATTING UTILITIES ==========
  getStatusClass(status: string): string {
    const classes: Record<string, string> = {
      'Draft': 'status-draft',
      'Pending Review': 'status-pending',
      'Active': 'status-active',
      'Expiring Soon': 'status-warning',
      'Expired': 'status-expired',
      'Terminated': 'status-terminated'
    };
    return classes[status] || 'status-default';
  }

  getDaysClass(days: number | null): string {
    if (days === null) return '';
    if (days < 0) return 'days-overdue';
    if (days === 0) return 'days-today';
    if (days <= 7) return 'days-critical';
    if (days <= 30) return 'days-warning';
    if (days <= 90) return 'days-caution';
    return 'days-ok';
  }

  formatCurrency(amount: number, currency: string = 'KES'): string {
    if (!amount || amount === 0) return '-';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency || 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }
}